import { useEffect, useMemo, useState } from 'react';
import type { Booking } from '@/services/bookings';
import { subscribeToTripBookings } from '@/services/bookings';
import type { Checklist } from '@/services/checklists';
import { subscribeToTripChecklist } from '@/services/checklists';
import type { ItineraryItem } from '@/services/itinerary';
import { subscribeToTripItineraryItems } from '@/services/itinerary';
import type { Trip } from '@/services/trips';
import {
  fetchOpenMeteoCurrentWeather,
  fetchWeatherForecast,
} from '@/services/weather';
import type { DayWeather } from '@/components/maps/DaySummaryCard';
import { buildDayBriefing, type DayBriefing } from '@/services/dayBriefing';
import type { TripBudgetSnapshot } from '@/types/budget';
import { getTripDayContext } from '@/utils/tripDayContext';
import { mergeCurrentWeatherIntoDay, weatherForecastToDayWeatherMap } from '@/utils/dayWeather';
import { resolveTripWeatherLocation } from '@/utils/tripWeatherLocation';
import type { TripAlert } from '@/utils/tripAlerts';

type UseDayBriefingOptions = {
  budgetSnapshot?: TripBudgetSnapshot;
  alerts?: TripAlert[];
  enabled?: boolean;
};

export function useDayBriefing(trip?: Trip | null, options: UseDayBriefingOptions = {}): DayBriefing | null {
  const [itineraryItems, setItineraryItems] = useState<ItineraryItem[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [weather, setWeather] = useState<DayWeather | null>(null);
  const enabled = options.enabled ?? true;

  useEffect(() => {
    if (!trip?.id || !enabled) {
      setItineraryItems([]);
      setBookings([]);
      setChecklist(null);
      return;
    }
    const unsubscribeItinerary = subscribeToTripItineraryItems(trip.id, setItineraryItems);
    const unsubscribeBookings = subscribeToTripBookings(trip.id, setBookings);
    const unsubscribeChecklist = subscribeToTripChecklist(trip.id, setChecklist);
    return () => {
      unsubscribeItinerary();
      unsubscribeBookings();
      unsubscribeChecklist();
    };
  }, [enabled, trip?.id]);

  const weatherKey = useMemo(() => {
    if (!trip?.id || !enabled) return null;
    const dayContext = getTripDayContext(trip);
    return [trip.id, dayContext.todayKey, dayContext.timeZone].join('|');
  }, [enabled, trip]);

  useEffect(() => {
    let cancelled = false;
    if (!trip || !weatherKey) {
      setWeather(null);
      return;
    }

    const loadWeather = async () => {
      try {
        const location = await resolveTripWeatherLocation(trip);
        if (!location) {
          if (!cancelled) setWeather(null);
          return;
        }

        if (__DEV__) {
          console.log('[useDayBriefing] Weather location', {
            tripId: trip.id,
            label: location.label,
            latitude: location.latitude,
            longitude: location.longitude,
            source: location.source,
          });
        }

        const dayContext = getTripDayContext(trip);
        const todayKey = dayContext.todayKey;
        const [forecast, liveCurrent] = await Promise.all([
          fetchWeatherForecast({
            latitude: location.latitude,
            longitude: location.longitude,
            city: location.city,
            countryCode: location.countryCode,
            days: 2,
            targetDate: todayKey,
          }),
          fetchOpenMeteoCurrentWeather(location.latitude, location.longitude),
        ]);
        const weatherMap = weatherForecastToDayWeatherMap(forecast);
        if (!cancelled) {
          setWeather(
            mergeCurrentWeatherIntoDay(weatherMap[todayKey] ?? null, forecast, liveCurrent),
          );
        }
      } catch (error) {
        if (__DEV__) {
          console.warn('[useDayBriefing] Weather unavailable', error);
        }
        if (!cancelled) setWeather(null);
      }
    };

    void loadWeather();
    return () => {
      cancelled = true;
    };
  }, [trip, weatherKey]);

  return useMemo(() => {
    if (!trip || !enabled) return null;
    return buildDayBriefing(trip, {
      itineraryItems,
      bookings,
      checklist,
      budgetSnapshot: options.budgetSnapshot,
      alerts: options.alerts,
      weather,
    });
  }, [bookings, checklist, enabled, itineraryItems, options.alerts, options.budgetSnapshot, trip, weather]);
}
